// src/components/LanguageSwitcher/LanguageSwitcher.jsx

import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./LanguageSwitcher.css";

const LANGUAGES = [
  {
    code: "es",
    label: "ES",
    name: "Español",
  },
  {
    code: "pt",
    label: "PT",
    name: "Português",
  },
  {
    code: "en",
    label: "EN",
    name: "English",
  },
];

export default function LanguageSwitcher({
  variant = "inline",
  className = "",
}) {
  const { i18n } = useTranslation();

  const menuId = useId();
  const switcherRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = i18n.language?.split("-")[0] || "es";

  const currentOption =
    LANGUAGES.find((language) => language.code === currentLanguage) ||
    LANGUAGES[0];

  const changeLanguage = async (languageCode) => {
    await i18n.changeLanguage(languageCode);

    localStorage.setItem("i18nextLng", languageCode);

    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen((currentValue) => !currentValue);
  };

  const handleButtonKeyDown = (event) => {
    if (
      event.key === "ArrowDown" ||
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      setIsOpen(true);
    }

    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event, languageCode, index) => {
    const optionButtons = switcherRef.current?.querySelectorAll(
      ".lang-switcher__option",
    );

    if (!optionButtons?.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();

      const nextIndex = (index + 1) % optionButtons.length;

      optionButtons[nextIndex]?.focus();
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      const previousIndex = index === 0 ? optionButtons.length - 1 : index - 1;

      optionButtons[previousIndex]?.focus();
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      changeLanguage(languageCode);
    }

    if (event.key === "Escape") {
      event.preventDefault();

      setIsOpen(false);

      switcherRef.current?.querySelector(".lang-switcher__trigger")?.focus();
    }
  };

  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      if (!switcherRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDownOutside);

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutside);

      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const activeOption = switcherRef.current?.querySelector(
      ".lang-switcher__option.is-active",
    );

    window.requestAnimationFrame(() => {
      activeOption?.focus();
    });
  }, [isOpen]);

  return (
    <div
      ref={switcherRef}
      className={["lang-switcher", variant, isOpen ? "is-open" : "", className]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="lang-switcher__trigger"
        aria-label={`Idioma actual: ${currentOption.name}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={toggleMenu}
        onKeyDown={handleButtonKeyDown}
      >
        <span>{currentOption.label}</span>

        <span className="lang-switcher__chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          id={menuId}
          className="lang-switcher__menu"
          role="listbox"
          aria-label="Seleccionar idioma"
        >
          {LANGUAGES.map((language, index) => {
            const isActive = language.code === currentLanguage;

            return (
              <button
                key={language.code}
                type="button"
                role="option"
                aria-selected={isActive}
                className={[
                  "lang-switcher__option",
                  isActive ? "is-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => changeLanguage(language.code)}
                onKeyDown={(event) =>
                  handleOptionKeyDown(event, language.code, index)
                }
              >
                <span>{language.label}</span>

                {isActive && (
                  <span
                    className="lang-switcher__active-dot"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
